import { QuestionType2UIConfig } from "../../../../../components/ui/question-type2-ui";
import { GeneralQuestionUIConfig, GeneralQuestionUIConfigOption } from "../../../../../cvp-webgames-core/Components/ui/react/core-general-question-ui";
import BaseScenarioPart from "../../../../../cvp-webgames-core/Scenario/BaseScenarioPart";
import { VideoSegmentItem, SingleVideoSegmentItem } from "../../../../../cvp-webgames-core/VideoSegment/VideoSegmentItem";
import { ScoringType } from "../../../../../misc/tools";
import { GameBaseScenario } from "../../../../GameBaseScenario";
import Scenario1PivBadge, { scenario1PivBadgeAudioCaching, scenario1PivBadgeVideoSegments } from "../../../scenario1-piv-badge";
import { PivBadgeComponentsStep1ComponentMainConfig, PivBadgeComponentsStep1ComponentPart1Config } from "../piv-badge-components-step1";

export default class PivBadgeComponentsStep1Part1 extends BaseScenarioPart<GameBaseScenario> {
	private stepName = 'Step 2'
	private get config(): PivBadgeComponentsStep1ComponentPart1Config {
		return this.mainConfig.part1Config;
	}
	constructor(scenario: GameBaseScenario, private onDone: () => void, private id: string, private mainConfig: PivBadgeComponentsStep1ComponentMainConfig) {
		super(scenario);
	}

	public get partId(): string {
		return this.id;
	}

	public get videoUrl(): string[] {
		return [this.config.videoUrl];
	}

	public start() {
		this.section1();
	}

	public section1() {
		const context = this;
        this.mainConfig.scenario.setAreaForCurrentScenario(6);
		this.mainConfig.scenario.uiWrapper.displayMixedMediaUI({
			title: this.mainConfig.scenario.props.name,
			subtitle: this.stepName,
			htmlData: `
				<p class="default-text add-pad">For the substitution test, you approach other employees and inquire as to whether they would do the same 
				thing in similar circumstances.<br/><br/>
				Health System Specialist #1: I always carry my PIV Badge with me. We must wear it all 
				the time according to our facility’s policies.<br/><br/>
				Health System Specialist #2: I developed the habit to take my PIV Badge with me wherever 
				I go. I never leave it in my computer.<br/><br/>
				Health System Specialist #3: My supervisor constantly reminds us of the importance of 
				carrying our PIV badge at all times. I never leave my office without my badge. Never.</p>
			`,
			talkingVideoSrc: scenario1PivBadgeVideoSegments.getItemByKey('MixedMedia').src,
			talkingVideoChapterId: scenario1PivBadgeVideoSegments.getItemByKey('MixedMedia').chapterIds[0],
			okButton: 'Continue',
			onContinueClicked: () => {
				context.section2();
			}
		})
	}

	public section2() {
		const context = this;
		this.mainConfig.scenario.uiWrapper.displayQuestonType2UI({
			title: this.mainConfig.scenario.props.name,
			subtitle: (this.stepName + ' - Question 1'),
			isYesCorrect: false,
			audioUrl: scenario1PivBadgeAudioCaching.getItemByKey(scenario1PivBadgeAudioCaching.audioKeys.question3).src,
			correctFeedback: `
				<p class="default-text">Correct. The other Health System Specialists stated they always secure 
				their computer and PIV badge when they step out of their office.</p>
			`,
			wrongFeedback: `
				<p class="default-text">This is not the best option. The other Health System Specialists stated 
				they always secure their computer and PIV badge when they step out of their office.</p>
			`,
			question: 'Given the information received from staff interviews, is it reasonable to conclude that a systems or cultural issue exists, and other employees in that work area would behave the same way in a similar situation?',
			onSubmitClicked: (isCorrect: boolean, feedback: string, selectedTitle: string, parentQuestion: QuestionType2UIConfig) => {
				const mentorInfo = scenario1PivBadgeVideoSegments.getItemByKey('Feedback');
				context.scenario.onChoiceSubmittedQuestionType2(isCorrect, feedback, selectedTitle, (this.stepName + ' - Question 1 '), parentQuestion, () => {
					context.section3();
				}, () => {
					context.section3();
				}, undefined, undefined, undefined, mentorInfo.src, mentorInfo.chapterIds[6], mentorInfo.chapterIds[7]);
			}
		}, 2);
	}

	public section3() {
		const context = this;
		this.mainConfig.scenario.uiWrapper.displayQuestonType1UI({
			title: this.mainConfig.scenario.props.name,
			subtitle: (this.stepName + ' - Question 2'),
			questionTitle: 'Based on the substitution test, the accountability for this action…',
			audioUrl: scenario1PivBadgeAudioCaching.getItemByKey(scenario1PivBadgeAudioCaching.audioKeys.question4).src,
			options: [
				{
					title: 'Lies completely with the employee.',
					description: 'Select and submit an option.',
					feedbackHtml: `
						<p class="default-text">Correct. The substitution test demonstrates that no 
						other employees would make a similar choice and there are no system or cultural 
						issues identified. Accountability for the employee’s choice rests completely 
						with them.</p>
					`,
					isCorrect: true,
					score: ScoringType.good
				},
				{
					title: 'Is shared between the employee and organizational leadership.',
					description: 'Select and submit an option.',
					feedbackHtml: `
						<p class="default-text">This is not the best option. The substitution test 
						demonstrates that no other employees would make a similar choice and there are 
						no system or cultural issues identified. Accountability for the employee’s 
						choice rests completely with them.</p>
					`,
					isCorrect: false,
					score: ScoringType.bad
				},
				{
					title: 'Lies solely with the leadership.',
					description: 'Select and submit an option.',
					feedbackHtml: `
						<p class="default-text">This is not the best option. The substitution test 
						demonstrates that there are no system or cultural issues identified. Accountability 
						for the employee’s choice rests completely with them.</p>
					`,
					isCorrect: false,
					score: ScoringType.bad
				}
			],
			
			onSubmitClicked: (allOptions: GeneralQuestionUIConfigOption[], selectedChoices: GeneralQuestionUIConfigOption[], parentQuestionConfig: GeneralQuestionUIConfig) => {
				if((this.mainConfig.scenario as Scenario1PivBadge).comp1Step1FirstSelectedChoiceIndex === -1) {
					(this.mainConfig.scenario as Scenario1PivBadge).comp1Step1FirstSelectedChoiceIndex = allOptions.indexOf(selectedChoices[0]);
				}
				const mentorInfo = scenario1PivBadgeVideoSegments.getItemByKey('Feedback');
				context.scenario.onChoiceSubmittedQuestionType1(selectedChoices[0], (this.stepName + ' - Question 2 '), parentQuestionConfig, () => {
					this.onDone();
				}, () => {
					this.onDone();
				}, undefined, undefined, undefined, mentorInfo.src, mentorInfo.chapterIds[8], mentorInfo.chapterIds[9], mentorInfo.chapterIds[10], 'Is shared between the employee and organizational leadership.');
			}
		}, 3);
	}
}